// src/theme/tint.js
// Colors for "tinted" labels: a light wash of a user-picked color (task
// categories etc.) with text in the same hue, dark enough to read.
//
// A plain alpha color like "#4caf5030" is fine as a background, but picking
// text for it with getContrastText() ignores the alpha and judges the solid
// color, so mid-tone hues got white text on an almost-white chip.
import { darken, decomposeColor, getContrastRatio, lighten, rgbToHex } from '@mui/material/styles';

const MIN_CONTRAST = 4.5; // WCAG AA for normal text

/** The opaque color you see when `color` at `opacity` sits on `background`. */
export function blendOver(color, background, opacity) {
  const fg = decomposeColor(rgbToHex(color)).values;
  const bg = decomposeColor(rgbToHex(background)).values;
  const mix = fg.slice(0, 3).map((c, i) => Math.round(c * opacity + bg[i] * (1 - opacity)));
  return `rgb(${mix.join(', ')})`;
}

/** `color`, darkened (light mode) or lightened (dark mode) until it reads on `background`. */
export function readableShade(color, background, mode) {
  let shade = color;
  for (let i = 0; i < 12 && getContrastRatio(shade, background) < MIN_CONTRAST; i += 1) {
    shade = mode === 'dark' ? lighten(shade, 0.2) : darken(shade, 0.2);
  }
  return shade;
}

/**
 * Background, border and text for a tinted label in `color`.
 * `opacity` is how strong the wash is (0.14 ≈ the old "30" hex suffix).
 */
export function tintStyles(theme, color, opacity = 0.14) {
  const surface = theme.palette.background.paper;
  const background = blendOver(color, surface, opacity);
  return {
    backgroundColor: background,
    border: `1px solid ${blendOver(color, surface, 0.45)}`,
    color: readableShade(color, background, theme.palette.mode),
  };
}

/** Split "#rrggbbaa" into its color and opacity; null for anything else. */
export function splitHexAlpha(value) {
  const match = /^#([0-9a-f]{6})([0-9a-f]{2})$/i.exec(value || '');
  return match ? { color: `#${match[1]}`, opacity: parseInt(match[2], 16) / 255 } : null;
}
